import { useEffect, useRef, useState } from "react";
import { Waves } from "lucide-react";
import { useLang } from "@/i18n";

/* Moteur de réservation standard Villa Mana — checkout CB opérationnel */
const ENGINE = "https://villamana.holidayfuture.com/";
/* Fiche logement sur le moteur standard : redirige aujourd'hui vers le portail
   Pro (fiche sombre à la marque) et reste fonctionnelle si le plan Pro est
   résilié (le moteur standard sert alors sa propre fiche). */
const PORTAL_LISTINGS = "https://villamana.holidayfuture.com/listings/";
const SEARCH_SCRIPT = "https://d2q3n06xhbi0am.cloudfront.net/widget.js?1640277196";
const CALENDAR_SCRIPT = "https://d2q3n06xhbi0am.cloudfront.net/calendar.js";

/* Le widget n'offre que l'action « checkout » (paiement direct). On réécrit
   son ouverture vers la FICHE du logement sur le portail (photos, description,
   prix) — de là, « Réserver maintenant » mène au checkout CB du moteur standard. */
function toListingUrl(url) {
  try {
    const u = new URL(String(url), window.location.href);
    const m = u.pathname.match(/\/checkout\/(\d+)/);
    if (!m) return null;
    const p = new URLSearchParams();
    const start = u.searchParams.get("start");
    const end = u.searchParams.get("end");
    const guests = u.searchParams.get("numberOfGuests");
    if (start) p.set("checkin", start);
    if (end) p.set("checkout", end);
    if (guests) p.set("numberOfGuests", guests);
    const qs = p.toString();
    return `${PORTAL_LISTINGS}${m[1]}${qs ? `?${qs}` : ""}`;
  } catch (e) {
    return null;
  }
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      if (existing.dataset.ready === "true") resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => {
      s.dataset.ready = "true";
      resolve();
    };
    s.onerror = () => reject(new Error(`load error ${src}`));
    document.body.appendChild(s);
  });
}

const GOLD = "#C2A675";

export function HostawaySearchEmbed() {
  const ref = useRef(null);

  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.innerHTML = '<div id="hostaway-booking-widget"></div>';
    loadScript(SEARCH_SCRIPT)
      .then(() => {
        if (!host || typeof window.searchBar !== "function") return;
        window.searchBar({
          baseUrl: ENGINE,
          showLocation: false,
          color: GOLD,
          rounded: true,
          openInNewTab: false,
          font: "Plus Jakarta Sans",
        });
      })
      .catch(() => {});
  }, []);

  return <div ref={ref} className="hostaway-embed" data-testid="hostaway-search-embed" />;
}

const TABS = [
  { id: 538706, fr: "La Villa · 3 chambres", en: "The Villa · 3 bedrooms" },
  { id: 538862, fr: "La Villa · 4 chambres (avec le Bungalow)", en: "The Villa · 4 bedrooms (with the Bungalow)" },
  { id: 538098, fr: "La Villa complète · 6 chambres", en: "The full Villa · 6 bedrooms" },
  { id: 536845, fr: "Le Bungalow · 1 chambre", en: "The Bungalow · 1 bedroom" },
  { id: 475669, fr: "Le Marina · 2 chambres", en: "The Marina · 2 bedrooms" },
];

export function HostawayCalendars({ listingId: controlledId, onListingChange }) {
  const { lang } = useLang();
  const [internalId, setInternalId] = useState(TABS[0].id);
  const listingId = controlledId ?? internalId;
  const setListingId = (id) => {
    setInternalId(id);
    if (onListingChange) onListingChange(id);
  };
  const ref = useRef(null);

  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.innerHTML = '<div id="hostaway-calendar-widget"></div>';
    document.querySelectorAll("script[data-vm-calendar]").forEach((s) => s.remove());
    /* Redirection checkout → fiche logement (window.open + ancres du widget) */
    const originalOpen = window.open;
    window.open = function (url, target, features) {
      const listing = toListingUrl(url);
      return originalOpen.call(this, listing || url, target, features);
    };
    const rewriteAnchors = () => {
      host.querySelectorAll('a[href*="/checkout/"]').forEach((a) => {
        const listing = toListingUrl(a.getAttribute("href"));
        if (listing) a.setAttribute("href", listing);
      });
    };
    rewriteAnchors();
    const observer = new MutationObserver(rewriteAnchors);
    observer.observe(host, { subtree: true, childList: true });
    const s = document.createElement("script");
    s.src = `${CALENDAR_SCRIPT}?cb=${Date.now()}`;
    s.async = true;
    s.dataset.vmCalendar = "true";
    s.onload = () => {
      if (!host || typeof window.hostawayCalendarWidget !== "function") return;
      window.hostawayCalendarWidget({
        baseUrl: ENGINE,
        listingId,
        numberOfMonths: 2,
        openInNewTab: true,
        font: "Plus Jakarta Sans",
        rounded: true,
        button: { action: "checkout", text: lang === "fr" ? "Réserver" : "Book now" },
        clearButtonText: lang === "fr" ? "Effacer les dates" : "Clear dates",
        color: { mainColor: GOLD, frameColor: GOLD, textColor: "#F9F8F3" },
      });
    };
    document.body.appendChild(s);
    return () => {
      observer.disconnect();
      window.open = originalOpen;
      if (host) host.innerHTML = "";
    };
  }, [listingId, lang]);

  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid="hostaway-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-testid={`hostaway-tab-${tab.id}`}
            onClick={() => setListingId(tab.id)}
            className={`font-mono text-[0.6rem] uppercase tracking-[0.15em] px-4 py-2.5 border transition-all duration-300 ${
              listingId === tab.id
                ? "border-gold bg-gold text-night"
                : "gold-hairline text-gold hover:bg-moss/50"
            }`}
          >
            {lang === "fr" ? tab.fr : tab.en}
          </button>
        ))}
      </div>
      {listingId === 475669 && (
        <div
          className="mt-4 flex items-center gap-3 bg-gold text-night px-4 py-3 marina-note-blink"
          data-testid="hostaway-marina-note"
        >
          <Waves size={18} strokeWidth={2.5} className="shrink-0" aria-hidden="true" />
          <span className="font-mono text-[0.68rem] uppercase tracking-[0.12em] font-semibold leading-snug">
            {lang === "fr"
              ? "La location du Marina n'inclut pas l'accès à nos bassins"
              : "The Marina rental does not include access to our pools"}
          </span>
        </div>
      )}
      <div
        ref={ref}
        className="hostaway-embed min-h-[420px] mt-6"
        data-testid="hostaway-calendar-embed"
      />
    </div>
  );
}
